import { TZDate } from '@date-fns/tz';
import {
  format,
  formatDuration as dateFnsFormatDuration,
  formatRelative,
  FormatRelativeToken,
  Locale,
  parseISO,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import padStart from 'lodash/padStart';

export const parseDate = (date: string | Date | number): Date => {
  if (date instanceof Date) return date;

  return new Date(date);
};

export function formatPeriod(p?: string | null) {
  if (!p) {
    return '';
  }

  if (p === 'monthly') {
    return 'per month';
  }
  if (p === 'yearly') {
    return 'per year';
  }
  return p;
}

export const formatDate = (date: Date | string | number, hideActualYear?: boolean): string => {
  const actualYear = new Date().getFullYear();
  const parsedDate = parseDate(date);
  if (hideActualYear && parsedDate.getFullYear() === actualYear) {
    return format(parsedDate, 'MMM d');
  }
  return format(parsedDate, 'MMM d, y');
};

// `new Date('2026-07-24')` is UTC midnight, which formats as the 23rd anywhere west of UTC. A
// date-only string names a calendar date, so it is parsed as local midnight; `parseISO` still honours
// the offset when the string carries a time.
const parseRangeEnd = (date: Date | string | number): Date =>
  typeof date === 'string' ? parseISO(date) : parseDate(date);

export const formatDateRange = (start: Date | string | number, end: Date | string | number): string => {
  const parsedStart = parseRangeEnd(start);
  const parsedEnd = parseRangeEnd(end);
  const actualYear = new Date().getFullYear();
  const hideYear = parsedStart.getFullYear() === actualYear && parsedEnd.getFullYear() === actualYear;

  return `${formatDate(parsedStart, hideYear)} - ${formatDate(parsedEnd, hideYear)}`;
};

type FormatDateTimeOptions = {
  hideSeconds?: boolean;
  hideYear?: boolean;
  includeTimeZone?: boolean;
  timeZone?: string;
  locale?: Locale;
};

export const formatDateTime = (date: Date | string, options?: FormatDateTimeOptions): string => {
  const parsedDate = parseDate(date);
  let dateFormat = 'MMM d, y h:mm:ss a';

  if (!options) return format(parsedDate, dateFormat);

  if (options.hideSeconds) {
    dateFormat = dateFormat.replace(':ss', '');
  }

  const actualYear = new Date().getFullYear();
  if (options.hideYear && parsedDate.getFullYear() === actualYear) {
    dateFormat = dateFormat.replace('y ', '');
  }

  if (options.timeZone) {
    return format(new TZDate(parsedDate, options.timeZone), dateFormat, { locale: options.locale });
  }

  if (options.includeTimeZone) {
    // getTimezoneOffset counts minutes WEST of UTC, so its sign is the opposite of the GMT label's.
    const offsetMinutes = -parsedDate.getTimezoneOffset();
    const sign = offsetMinutes < 0 ? '-' : '+';
    const hours = Math.trunc(Math.abs(offsetMinutes) / 60);
    // Zones like Asia/Kolkata (+05:30) and Australia/Eucla (+08:45) are offset by part of an hour,
    // and dropping those minutes leaves the label disagreeing with the clock time next to it.
    const minutes = Math.abs(offsetMinutes) % 60;
    const offset = minutes === 0 ? `${hours}` : `${hours}:${padStart(String(minutes), 2, '0')}`;
    const gmtOffset = offsetMinutes === 0 ? 'GMT' : `GMT${sign}${offset}`;

    const formattedDate = format(parsedDate, dateFormat, { locale: options.locale });
    return `${formattedDate} (${gmtOffset})`;
  }

  return format(parsedDate, dateFormat, { locale: options.locale });
};

export const formatTime = (date: Date | string, options?: FormatDateTimeOptions): string => {
  const parsedDate = parseDate(date);
  let dateFormat = 'h:mm:ss a';

  if (options?.hideSeconds) {
    dateFormat = dateFormat.replace(':ss', '');
  }

  return format(parsedDate, dateFormat, { locale: options?.locale });
};

export const formatDateTimeToInput = (date: Date | string): string => {
  const parsedDate = parseDate(date);
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm");
};

type FormatArgs = number | { decimalPlaces: number; hideZeroFractional?: boolean };
export const formatNumber = (number: number, decimalPlacesOrOpts: FormatArgs = 0) => {
  const decimalPlaces =
    typeof decimalPlacesOrOpts === 'number' ? decimalPlacesOrOpts : decimalPlacesOrOpts.decimalPlaces || 0;
  const hideZeroFractional = typeof decimalPlacesOrOpts === 'object' ? decimalPlacesOrOpts.hideZeroFractional : false;
  const trailingZeroDisplay = hideZeroFractional ? 'stripIfInteger' : 'auto';
  return number.toLocaleString('en', {
    minimumFractionDigits: hideZeroFractional ? 0 : decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    // @ts-expect-error 97% supported but not in TS for some reason
    trailingZeroDisplay,
  });
};

export const formatMoney = (number: number, decimalPlacesOrOpts: FormatArgs = 0) =>
  `${formatNumber(number, decimalPlacesOrOpts)} USD`;

// Certificate serials arrive as decimal strings but Keychain Access and `openssl x509 -serial`
// show them as uppercase, byte-aligned (even-length) hex, so we render the same form for parity.
export const formatCertificateSerial = (serial: string): string => {
  if (!serial) {
    return serial;
  }

  try {
    const hex = BigInt(serial.trim()).toString(16).toUpperCase();
    return hex.length % 2 === 0 ? hex : `0${hex}`;
  } catch {
    // BigInt throws on anything that isn't a plain integer; degrade to the raw value rather than crash.
    return serial;
  }
};

export const formatInteger = (number: number): string => {
  if (number >= 1_000_000) {
    const formatted = (number / 1_000_000).toFixed(1).replace(/\.0$/, '');
    return `${formatted}M`;
  } else if (number >= 100_000) {
    const formatted = (number / 1_000).toFixed(0);
    return `${formatted}K`;
  }

  return number.toString();
};

export const formatDuration = (duration: number, zeroPaddingCount = 1): string => {
  const parts = [];
  let time = duration * 1000;
  const milliseconds = Math.round(time % 1000);
  time -= milliseconds;
  time /= 1000;
  const seconds = Math.round(time % 60);
  time -= seconds;
  time /= 60;
  const minutes = Math.round(time % 60);
  time -= minutes;
  time /= 60;
  const hours = Math.round(time % 24);
  time -= hours;
  time /= 24;
  const days = Math.round(time);

  if (days > 0) {
    parts.push(`${padStart(String(days), zeroPaddingCount, '0')}d`);
  }
  if (hours > 0) {
    parts.push(`${padStart(String(hours), zeroPaddingCount, '0')}h`);
  }
  if (minutes > 0) {
    parts.push(`${padStart(String(minutes), zeroPaddingCount, '0')}m`);
  }

  if (seconds > 0 || minutes > 0) {
    let secondsWithMilliseconds = seconds;
    if (milliseconds > 0) {
      secondsWithMilliseconds += Math.round(milliseconds / 100) / 10;
    }
    parts.push(`${padStart(String(secondsWithMilliseconds || 0), zeroPaddingCount, '0')}s`);
  }

  if (milliseconds > 0 && parts.length === 0) {
    parts.push(`${padStart(String(milliseconds || 0), zeroPaddingCount, '0')}ms`);
  }

  return parts.slice(0, 2).join(' ') || '0s';
};

const COMPACT_DURATION_SUFFIXES: Record<string, string> = {
  xDays: 'd',
  xHours: 'h',
  xMinutes: 'm',
  xSeconds: 's',
};

const compactLocale: Locale = {
  ...enUS,
  formatDistance: (token: string, count: number) => {
    const suffix = COMPACT_DURATION_SUFFIXES[token];

    return suffix ? `${count}${suffix}` : '';
  },
};

export const formatSavedTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) {
    return '0';
  }

  const totalSeconds = Math.max(0, Math.floor(seconds));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const duration = days === 0 ? { hours, minutes, seconds: secs } : { days, hours, minutes };

  return dateFnsFormatDuration(duration, { locale: compactLocale, delimiter: ' ' }) || '0';
};

export const formatPercent = (value: number): string => {
  return `${Math.trunc(value * 100)}%`;
};

const FormatRelativeTokenMap: Record<FormatRelativeToken, string> = {
  lastWeek: 'EEEE, MMMM do',
  yesterday: "'Yesterday'",
  today: "'Today'",
  tomorrow: "'Tomorrow'",
  nextWeek: 'EEEE, MMMM do',
  other: 'MMMM do, yyyy',
};

export const formatRelativeCustom = (
  date: number | Date,
  baseDate: number | Date = new Date(),
  tokenMap: Record<FormatRelativeToken, string> = FormatRelativeTokenMap,
) => {
  return formatRelative(date, baseDate, {
    weekStartsOn: 1,
    locale: {
      ...enUS,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      formatRelative: (token) => tokenMap[token],
    },
  });
};

const FormatRelativeWithTimeTokenMap = {
  lastWeek: "EEEE, MMMM do 'at' h:mm aa",
  yesterday: "'Yesterday at' h:mm aa",
  today: "'Today at' h:mm aa",
  tomorrow: "'Tomorrow at' h:mm aa",
  nextWeek: "EEEE, MMMM do 'at' h:mm aa",
  other: "MMMM do, yyyy 'at' h:mm aa",
};

export const formatRelativeWithTime = (date: number | Date, baseDate: number | Date = new Date()) => {
  return formatRelativeCustom(date, baseDate, FormatRelativeWithTimeTokenMap);
};
